/**
 * Tokens that arrive in the address bar from a letter.
 *
 * Password recovery lands on `/?reset=<token>`, email confirmation on
 * `/?verify=<token>` and an invitation on `/?invite=<token>`. All three are
 * 64-character hex strings produced by `crypto.randomBytes(32)` on the server, so
 * anything that does not look like one is ignored rather than sent to the API.
 */

export type LinkTokenName = 'reset' | 'verify' | 'invite';

const TOKEN_SHAPE = /^[a-f0-9]{40,256}$/i;

export function readLinkToken(name: LinkTokenName): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get(name);
  const token = (raw || '').trim();
  return TOKEN_SHAPE.test(token) ? token : null;
}

/**
 * Drop the token from the address bar once it has been used, so a reload does not
 * replay a consumed link and the secret does not sit in the browser history or
 * get pasted into a support ticket.
 */
export function clearLinkToken(name: LinkTokenName): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has(name)) return;
  url.searchParams.delete(name);
  const search = url.searchParams.toString();
  window.history.replaceState({}, '', `${url.pathname}${search ? `?${search}` : ''}${url.hash}`);
}

const NOTICE_KEY = 'clientix_notice';

/**
 * One-shot message handed from the sign-in screen to the application shell.
 * Needed because registering through an invitation immediately unmounts the auth
 * screen, and the outcome of joining the team still has to reach the user.
 */
export function queueNotice(notice: { tone: 'info' | 'warn'; text: string }): void {
  try {
    window.sessionStorage.setItem(NOTICE_KEY, JSON.stringify(notice));
  } catch {
    // Private mode with storage disabled: the notice is not worth an exception.
  }
}

export function takeNotice(): { tone: 'info' | 'warn'; text: string } | null {
  try {
    const raw = window.sessionStorage.getItem(NOTICE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(NOTICE_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.text !== 'string') return null;
    return { tone: parsed.tone === 'warn' ? 'warn' : 'info', text: parsed.text };
  } catch {
    return null;
  }
}
